'use client';

import { c as _c } from "react/compiler-runtime";
import { createBackerRewardScope, type ItemsRenderProps, useGetBackerRewards } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { PortfolioPanelHeader } from '@/components/portfolio/shared/portfolio-panel-header';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Panel, PanelContent } from '@/components/ui/panel';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { Link } from '@/i18n/navigation';
import { logger } from '@/lib/telemetry/logger';
import { BackedProposalRow } from './backed-proposal-row';
const BACKED_PROPOSAL_FIELDS = ['marketPubkey', 'marketTitle', 'marketImageUrl', 'marketState', 'backedD6', 'backedTotalD6', 'earnedD6', 'claimedTotalD6'] as const;
interface BackedProposalsPanelProps {
  userId: string;
  wallet: string | null;
  walletPubkeys: readonly string[];
  own: boolean;
}
export function BackedProposalsPanel(props) {
  const $ = _c(3);
  const enabled = useFeatureGate("backed_proposals");
  if (enabled !== true || props.userId === "") {
    return null;
  }
  const t0 = `${props.userId}:${props.wallet ?? "all"}`;
  let t1;
  if ($[0] !== props || $[1] !== t0) {
    t1 = <BackedProposalsContent key={t0} {...props} />;
    $[0] = props;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function BackedProposalsContent(t0) {
  const $ = _c(39);
  const {
    userId,
    wallet,
    walletPubkeys,
    own
  } = t0;
  const t = useTranslations("profile.backedProposals");
  const t1 = wallet ?? userId;
  let t2;
  if ($[0] !== userId || $[1] !== walletPubkeys) {
    t2 = createBackerRewardScope(userId, walletPubkeys);
    $[0] = userId;
    $[1] = walletPubkeys;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== walletPubkeys) {
    t3 = walletPubkeys.join(",");
    $[3] = walletPubkeys;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      userId: t1,
      rewardScope: t2,
      walletPubkeysCsv: t3,
      sortBy: "backed",
      fields: BACKED_PROPOSAL_FIELDS
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const query = useGetBackerRewards(t4);
  const failed = query.isError;
  let t5;
  let t6;
  if ($[9] !== failed || $[10] !== own || $[11] !== wallet) {
    t5 = () => {
      if (failed) {
        logger.warn("portfolio.backed_proposals_query_failed", {
          scope: wallet === null ? "all" : "single",
          viewerMode: own ? "own" : "other"
        });
      }
    };
    t6 = [failed, wallet, own];
    $[9] = failed;
    $[10] = own;
    $[11] = wallet;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] !== query || $[15] !== wallet) {
    t7 = async function loadMore() {
      try {
        if (query.nextPageError !== null) {
          await query.retryNextPage();
        } else {
          await query.fetchNextPage();
        }
      } catch {
        logger.warn("portfolio.backed_proposals_page_failed", {
          scope: wallet === null ? "all" : "single"
        });
      }
    };
    $[14] = query;
    $[15] = wallet;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const loadMore = t7;
  let t8;
  if ($[17] !== query) {
    t8 = function retry() {
      query.refetch();
    };
    $[17] = query;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const retry = t8;
  let t9;
  if ($[19] !== t) {
    t9 = t("title");
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <PortfolioPanelHeader title={t9} />;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== failed || $[24] !== loadMore || $[25] !== own || $[26] !== query.Items || $[27] !== query.hasNextPage || $[28] !== query.isFetchingNextPage || $[29] !== query.isPending || $[30] !== query.nextPageError || $[31] !== query.queryKey || $[32] !== query.rows || $[33] !== retry || $[34] !== t) {
    t11 = query.isPending ? <output aria-label={t("loading")} className="flex flex-col gap-2"><Skeleton className="h-24 w-full rounded-card" /><Skeleton className="h-24 w-full rounded-card" /></output> : failed ? <EmptyState size="compact" title={t("error")} action={<AnimatedButton variant="secondary" onClick={retry}>{t("retry")}</AnimatedButton>} /> : query.rows.length === 0 ? <EmptyState size="compact" title={t("emptyTitle")} description={own ? t("emptyOwn") : t("emptyOther")} action={own ? <AnimatedButton variant="secondary" asChild={true}><Link href="/proposals">{t("browse")}</Link></AnimatedButton> : undefined} /> : <><div aria-hidden="true" className="hidden grid-cols-[minmax(0,1fr)_8rem_8rem_8rem] gap-6 px-4 text-caption text-foreground-tertiary lg:grid"><span>{t("proposal")}</span><span>{own ? t("yourBacking") : t("amountBacked")}</span><span>{t("vaultShare")}</span><span>{t("earned")}</span></div><ul className="flex flex-col gap-2"><query.Items queryKey={query.queryKey}>{t12 => {
            const {
              id,
              ref
            } = t12;
            return <li key={id} ref={ref} className="min-w-0"><BackedProposalRow pubkey={id} own={own} /></li>;
          }}</query.Items></ul>{query.nextPageError !== null ? <p role="alert" className="text-caption text-foreground-secondary">{t("error")}</p> : null}{query.hasNextPage ? <div className="flex justify-center"><AnimatedButton variant="secondary" loading={query.isFetchingNextPage} disabled={query.isFetchingNextPage} onClick={() => void loadMore()}>{query.nextPageError !== null ? t("retry") : t("loadMore")}</AnimatedButton></div> : null}</>;
    $[23] = failed;
    $[24] = loadMore;
    $[25] = own;
    $[26] = query.Items;
    $[27] = query.hasNextPage;
    $[28] = query.isFetchingNextPage;
    $[29] = query.isPending;
    $[30] = query.nextPageError;
    $[31] = query.queryKey;
    $[32] = query.rows;
    $[33] = retry;
    $[34] = t;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11) {
    t12 = <Panel data-testid="portfolio-backed-proposals"><PanelContent className="flex flex-col gap-4">{t10}{t11}</PanelContent></Panel>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
}